import React, { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import Login from '../Login';
import { Button, Card, CardContent, CircularProgress, Grid } from '@mui/material';
import { AccountForm } from './AcountForm';
import { useMutation, useQuery } from '@apollo/client';
import { GQL_GET_ACCOUNT } from '../../graphql/queries/accounts';
import { GQL_UPDATE_ACCOUNT } from '../../graphql/mutations/accounts';
import UTextField2 from '../../utils/UTextField2';
import styles from './index.module.css';

const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
function AccountDetail() {
  const passwordRef = useRef<HTMLInputElement>();
  const initialState: AccountForm = {
    name: '',
    email: '',
    password: '',
    confirm: '',
  };
  const methods = useForm<AccountForm>({
    defaultValues: initialState,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    shouldFocusError: true,
  });
  const { handleSubmit, reset } = methods;
  const { authorized, removeToken } = useContext(AuthenticationContext);
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState();
  const query = GQL_GET_ACCOUNT;
  useQuery(query, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(`Failed to load data ${error.message}`);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      setLoading(false);
      setClientName(data.account.client.name);
      const form = {
        name: data.account.name,
        email: data.account.email,
      };
      reset(form);
    },
    skip: !authorized,
  });
  const [updateAccount] = useMutation(GQL_UPDATE_ACCOUNT, {
    refetchQueries: [{ query: GQL_GET_ACCOUNT }],
    awaitRefetchQueries: true,
  });

  if (!authorized) return <Login />;

  const onSubmit = (account: AccountForm) => {
    const variables: any = {
      input: {
        name: account.name,
        email: account.email,
        password: account.password,
      },
    };
    reset({ ...account, password: '', confirm: '' });
    variables.input.id = id;
    if (!variables.input.password) delete variables.input.password;
    updateAccount({ variables })
      .then(() => {
        setLoading(false);
        setError('');
        setSuccess('Account updated!');
      })
      .catch((err) => {
        setLoading(false);
        setError(`Failed to update account ${err.message}`);
        console.error(err);
      });
  };

  const onValidatePassword: any = {
    required: (value: string): string | null => {
      if (id == 'new') {
        return value ? null : 'This field is required';
      } else return null;
    },
    minLength: (value: string): string | null => {
      const min = 8;
      const message = `Password must have at least ${min} characters`;
      if (id == 'new') {
        return value.length >= min ? null : message;
      } else {
        if (!value) return null;
        return value.length >= min ? null : message;
      }
    },
    maxLength: (value: string): string | null => {
      const max = 25;
      const message = `Password must have at max ${max} characters`;
      if (id == 'new') {
        return value.length <= max ? null : message;
      } else {
        if (!value) return null;
        return value.length <= max ? null : message;
      }
    },
    regex: (value: string): string | null => {
      const message =
        'Password must have at least one number, one uppercase, one lowercase and one special (@$!%*?&) character';
      if (value) {
        if (passwordValidationRegex.test(value)) return null;
        else return message;
      } else {
        return null;
      }
    },
  };

  const onValidateConfirm: any = {
    required: (value: string): string | null => {
      const message = 'Password must be confirmed';
      if (id == 'new') {
        return !value ? message : null;
      } else {
        if (passwordRef?.current?.value) {
          return !value ? message : null;
        } else {
          return null;
        }
      }
    },
    matches: (value: string): string | null => {
      if (value) {
        return value == passwordRef?.current?.value ? null : 'Passwords not matching';
      } else return null;
    },
  };

  return (
    <Card>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className={styles.card}>
            <Grid container spacing={2}>
              <Grid container item spacing={2} alignItems="center">
                <Grid item xs={12} sm>
                  <h4>
                    <span>{clientName || ''}</span>
                  </h4>
                  <h1>Account Settings</h1>
                </Grid>
                <Grid item xs="auto" sm="auto"></Grid>
                <Grid item flexGrow={{ xs: 1, sm: 0 }} flexShrink={{ sm: 1 }}></Grid>
                <Grid item xs="auto" sm="auto">
                  <Button type="submit" color="secondary" disabled={loading} variant="contained">
                    {id == 'new' ? 'Add' : 'Update'}
                  </Button>
                </Grid>
              </Grid>
              {loading && (
                <Grid item container xs={12} minHeight="300px" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Grid>
              )}
              {success && (
                <Grid item xs={12}>
                  <h3>{success}</h3>
                </Grid>
              )}
              {error && (
                <Grid item xs={12}>
                  <h3>{error}</h3>
                </Grid>
              )}
              {!loading && (
                <>
                  <Grid item xs={12} sm={6}>
                    <UTextField2
                      required
                      fullWidth
                      label="name"
                      shrink={true}
                      name="name"
                      options={{
                        required: 'Name is required',
                        minLength: { value: 3, message: 'Name must have at least 3 characters' },
                        maxLength: { value: 50, message: 'Name must have at max 50 characters' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <UTextField2
                      required
                      fullWidth
                      label="e-mail"
                      name="email"
                      shrink={true}
                      options={{
                        required: 'e-mail is required',
                        maxLength: { value: 50, message: 'E-mail must have at max 50 characters' },
                        pattern: {
                          value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                          message: 'Enter a valid e-mail',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <UTextField2
                      fullWidth
                      type="password"
                      label="password"
                      name="password"
                      ref={passwordRef}
                      shrink={true}
                      options={{
                        validate: onValidatePassword,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <UTextField2
                      label="Confirm"
                      type="password"
                      fullWidth
                      shrink={true}
                      name="confirm"
                      options={{
                        validate: onValidateConfirm,
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </form>
      </FormProvider>
    </Card>
  );
}

export default AccountDetail;
